import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getIn } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import AdFileUpload from '../AdFileUpload'
import RadioBlock from '../../../components/Form/RadioBlock'
import FacebookImageFilesLibrary from './FacebookImageFilesLibrary'
import FacebookVideoFilesLibrary from './FacebookVideoFilesLibrary'

import { concat } from '../../../helpers/common'
import { getFileError } from '../../Facebook/AdForms/helpers'
import { getFileUploadAccept } from './helpers'

import { fileUpload } from '../../../modules/actions/files'
import { selectedFacebookAdAccountSelector } from '../../../modules/selectors/adAccounts'

import { fileUploadProgressSelector } from '../../../modules/selectors/files'

import {
  AD_FILE,
  IMAGE_HASH,
  FILE_ID,
  FILE_NAME,
  FILE_URL,
  MEDIA_FILE_IMAGE_USE_LIBRARY,
  MEDIA_FILE_OPTION,
  MEDIA_FILE_UPLOAD,
  MEDIA_FILE_VIDEO_USE_LIBRARY,
  MEDIA_TYPE,
  UPLOADED_MEDIA_TYPE,
  VIDEO_PICTURE
} from '../AdFileUpload/fields'
import { initialFileValues, initialMediaValues } from '../../Facebook/AdForms/fields'
import { FACEBOOK_PLATFORM } from '../../../constants/selectLists/platformList'

import useStyles from './styles'

// To make it possible for component to work with nested objects
// in formik values, use the built-in formik function getIn
const FacebookMediaUpload = ({
  formik,
  itemPath,
  item,
  isSingleMedia,
  isItemActive,
  mediaUrlError,
  mediaFileError,
  mediaFileErrorField,
  recommendedSize,
  allowVideo = true,
  allowImage = true
}) => {
  const dispatch = useDispatch()
  const { values, setFieldValue, setValues, errors } = formik
  const { t } = useTranslation()

  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  // Constant that represent path ot item in formik values
  const fileValueName = isSingleMedia ? FILE_NAME : concat(itemPath, '.', FILE_NAME)
  const mediaOptionValueName = isSingleMedia ? MEDIA_FILE_OPTION : concat(itemPath, '.', MEDIA_FILE_OPTION)

  const mediaName = getIn(values, fileValueName)
  const selectedMediaOption = getIn(values, mediaOptionValueName)

  const { [mediaName]: fileUploadProgress } = useSelector(fileUploadProgressSelector)
  const fileIsUploaded = fileUploadProgress === 100

  const classes = useStyles()

  const accept = useMemo(() => getFileUploadAccept(allowImage, allowVideo), [allowImage, allowVideo])

  useEffect(() => {
    // When AdFileUpload component validate new field, copy errors to item if they exists
    if (isItemActive && getFileError(errors)) {
      setFieldValue(mediaFileErrorField, getFileError(errors))
    } else if (fileIsUploaded) {
      // clear error after successful upload
      mediaFileErrorField && setFieldValue(mediaFileErrorField, '')
    }
  }, [isItemActive, mediaFileError, itemPath, mediaFileErrorField, errors, setFieldValue, fileIsUploaded])

  const setMediaFiles = useCallback(
    // set Media files to Formik according to media quantity
    newFileValues => {
      if (isSingleMedia) {
        setValues({ ...values, ...newFileValues })
      } else {
        const updatedItem = {
          ...getIn(values, itemPath),
          ...newFileValues
        }
        setFieldValue(itemPath, updatedItem)
      }
    },
    [isSingleMedia, itemPath, values, setFieldValue, setValues]
  )

  const handleMediaRemove = useCallback(() => {
    // set initial media file values for deleted file
    if (isSingleMedia) {
      setValues({ ...values, ...initialFileValues, ...initialMediaValues })
    } else {
      const initialItemMediaValues = { ...item, ...initialFileValues, ...initialMediaValues }
      setFieldValue(itemPath, initialItemMediaValues)
    }
  }, [values, isSingleMedia, setValues, itemPath, setFieldValue, item])

  const handleMediaStartUpload = useCallback(
    // file is loading to Server, on success file uploading the handleMediaUploaded is called and set rest fields
    (file, newFileValues) => {
      // remove file when select file from another type
      handleMediaRemove()

      const newMediaValues = {
        ...initialFileValues,
        ...initialMediaValues,
        ...newFileValues
      }
      setMediaFiles(newMediaValues)

      // upload file to server
      dispatch(
        fileUpload(file, {
          isPlatformRelated: true,
          platform: FACEBOOK_PLATFORM,
          account: selectedAdAccountId
        })
      )
    },
    [dispatch, handleMediaRemove, setMediaFiles, selectedAdAccountId]
  )

  const handleMediaUploaded = useCallback(
    uploadedFile => {
      const newFileValues = {
        [FILE_URL]: uploadedFile.url,
        [IMAGE_HASH]: uploadedFile.hash,
        [FILE_ID]: uploadedFile.id,
        // if type of media is video, we have additional property VIDEO_PICTURE
        ...(uploadedFile.picture && { [VIDEO_PICTURE]: uploadedFile.picture })
      }

      setMediaFiles(newFileValues)
    },
    [setMediaFiles]
  )

  const handleExistingMediaSelected = useCallback(
    (selectedMedia, mediaType) => {
      const { name, id, url, hash, picture } = selectedMedia
      // remove file when select file from another type
      handleMediaRemove()
      const newFileValues = {
        ...initialFileValues,
        ...initialMediaValues,
        [FILE_NAME]: name || id,
        [FILE_URL]: url,
        [IMAGE_HASH]: hash,
        [FILE_ID]: id,
        [UPLOADED_MEDIA_TYPE]: selectedMediaOption,
        [MEDIA_TYPE]: mediaType,
        // if type of media is video, we have additional property VIDEO_PICTURE
        ...(picture && { [VIDEO_PICTURE]: picture })
      }

      setMediaFiles(newFileValues)
    },
    [handleMediaRemove, setMediaFiles, selectedMediaOption]
  )

  return (
    <div className={classes.media}>
      <RadioBlock
        setFieldValue={setFieldValue}
        id={`${itemPath}_radio_media_file_upload`}
        name={mediaOptionValueName}
        value={MEDIA_FILE_UPLOAD}
        selectedValue={selectedMediaOption}
        label={t('Upload a file from my device')}
      >
        <AdFileUpload
          values={formik.values}
          validateForm={formik.validateForm}
          onFileStartUpload={handleMediaStartUpload}
          onFileUploaded={handleMediaUploaded}
          onFileRemove={handleMediaRemove}
          /* In case if user try to load not valid media,
          we have both mediaFileError and mediaUrlError,
          but we must to show an error about wrong media properties */
          error={mediaFileError || mediaUrlError}
          mediaType={MEDIA_FILE_UPLOAD}
          fileKey={isSingleMedia ? AD_FILE : concat(itemPath, '.', AD_FILE)}
          fileNameKey={isSingleMedia ? FILE_NAME : concat(itemPath, '.', FILE_NAME)}
          fileURLKey={isSingleMedia ? FILE_URL : concat(itemPath, '.', FILE_URL)}
          fileTypeKey={isSingleMedia ? MEDIA_TYPE : concat(itemPath, '.', MEDIA_TYPE)}
          uploadedMediaTypeKey={isSingleMedia ? UPLOADED_MEDIA_TYPE : concat(itemPath, '.', UPLOADED_MEDIA_TYPE)}
          recommendedSize={recommendedSize}
          accept={accept}
          platform={FACEBOOK_PLATFORM}
        />
      </RadioBlock>
      {allowImage && (
        <RadioBlock
          setFieldValue={setFieldValue}
          id={`${itemPath}_radio_media_file_use_library_image`}
          name={mediaOptionValueName}
          value={MEDIA_FILE_IMAGE_USE_LIBRARY}
          selectedValue={selectedMediaOption}
          label={t('Use an existing image from the library')}
          touched={mediaFileError || mediaUrlError}
          error={mediaFileError || mediaUrlError}
        >
          <FacebookImageFilesLibrary
            values={values}
            onMediaSelect={handleExistingMediaSelected}
            onFileRemove={handleMediaRemove}
            mediaType={MEDIA_FILE_IMAGE_USE_LIBRARY}
            fileNameKey={isSingleMedia ? FILE_NAME : concat(itemPath, '.', FILE_NAME)}
            fileURLKey={isSingleMedia ? FILE_URL : concat(itemPath, '.', FILE_URL)}
            uploadedMediaTypeKey={isSingleMedia ? UPLOADED_MEDIA_TYPE : concat(itemPath, '.', UPLOADED_MEDIA_TYPE)}
          />
        </RadioBlock>
      )}
      <RadioBlock
        setFieldValue={setFieldValue}
        id={`${itemPath}_radio_media_file_video_use_library_video`}
        name={mediaOptionValueName}
        value={MEDIA_FILE_VIDEO_USE_LIBRARY}
        selectedValue={selectedMediaOption}
        label={t('Use an existing video from the library')}
        touched={mediaFileError || mediaUrlError}
        error={mediaFileError || mediaUrlError}
      >
        <FacebookVideoFilesLibrary
          values={values}
          onMediaSelect={handleExistingMediaSelected}
          onFileRemove={handleMediaRemove}
          mediaType={MEDIA_FILE_VIDEO_USE_LIBRARY}
          fileNameKey={isSingleMedia ? FILE_NAME : concat(itemPath, '.', FILE_NAME)}
          fileURLKey={isSingleMedia ? FILE_URL : concat(itemPath, '.', FILE_URL)}
          uploadedMediaTypeKey={isSingleMedia ? UPLOADED_MEDIA_TYPE : concat(itemPath, '.', UPLOADED_MEDIA_TYPE)}
        />
      </RadioBlock>
    </div>
  )
}

FacebookMediaUpload.propTypes = {
  formik: PropTypes.object,
  itemPath: PropTypes.string,
  item: PropTypes.object,
  isItemActive: PropTypes.bool,
  mediaUrlError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  mediaFileError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  mediaFileErrorField: PropTypes.string,
  recommendedSize: PropTypes.string,
  accept: PropTypes.string
}

export default FacebookMediaUpload
